import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Space,
  Typography,
  Button,
  Select,
  DatePicker,
  message,
  Empty,
  Tag,
} from "antd";
import {
  ReloadOutlined,
  MailOutlined,
  FileTextOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  CloudDownloadOutlined,
  HistoryOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  MAILING_LTV_EXPORT_TEMPLATE,
  exportTemplateToCsv,
  exportToPDF,
  getFilteredRowsFromGrid,
} from "./exportUtils";
import { formatCompactCurrency, formatPasRadeBrandName } from "./salesDashboardUtils";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import { getMailingLTVColumns, defaultColDef } from "./salesDashboardColumns";
import {
  getMailingResponseDataByFileIds,
  listPasRadeLTVDownloads,
  triggerPasRadeFetch,
  listPasRadeBrands,
  exportMergedMailingLtvExcel,
} from "../../../../core/api/report";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const { Text } = Typography;
const { Option } = Select;

const LATEST_KEY = "__latest__";

const MergedMailingLTV = ({ brandIds, brandNameMap, dateRange, refreshKey, isActive }) => {
  const [loading, setLoading] = useState(false);
  const [mailingData, setMailingData] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [fetchingPasRade, setFetchingPasRade] = useState(false);
  const [fetchProgress, setFetchProgress] = useState("");

  const [downloads, setDownloads] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(LATEST_KEY);
  const [loadingDownloads, setLoadingDownloads] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const gridRef = useRef(null);
  const prevActiveRef = useRef(isActive);
  const brandNameMapRef = useRef(brandNameMap);
  useEffect(() => { brandNameMapRef.current = brandNameMap; }, [brandNameMap]);
  const columnDefs = useMemo(() => getMailingLTVColumns(), []);

  useEffect(() => {
    if (isActive && !prevActiveRef.current) {
      const t = setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
      return () => clearTimeout(t);
    }
    prevActiveRef.current = isActive;
  }, [isActive]);

  const fetchDownloadsList = useCallback(async (forceLatest = false) => {
    if (!brandIds.length) {
      setDownloads([]);
      setSelectedDate(null);
      setSelectedVersion(LATEST_KEY);
      return;
    }
    setLoadingDownloads(true);
    try {
      const res = await listPasRadeLTVDownloads(brandIds);
      const list = res.data || [];
      setDownloads(list);
      setSelectedDate((currentDate) =>
        list.length > 0 && (forceLatest || !currentDate)
          ? dayjs(list[0].created_at).format("YYYY-MM-DD")
          : currentDate
      );
      setSelectedVersion(LATEST_KEY);
    } catch (err) {
      console.error("Failed to load LTV downloads:", err);
      setDownloads([]);
    } finally {
      setLoadingDownloads(false);
    }
  }, [brandIds]);

  useEffect(() => {
    fetchDownloadsList();
  }, [fetchDownloadsList, refreshKey]);

  const availableDates = useMemo(() => {
    const dateSet = new Set();
    downloads.forEach((d) => dateSet.add(dayjs(d.created_at).format("YYYY-MM-DD")));
    return [...dateSet].sort((a, b) => b.localeCompare(a));
  }, [downloads]);

  const downloadsForDate = useMemo(() => {
    if (!selectedDate) return downloads;
    return downloads.filter((d) => dayjs(d.created_at).format("YYYY-MM-DD") === selectedDate);
  }, [downloads, selectedDate]);

  const fileIdToBrandMeta = useMemo(() => {
    const meta = {};
    downloads.forEach((d) => {
      meta[d.id] = { brandId: d.brand_id, brandName: d.brand_name };
    });
    return meta;
  }, [downloads]);

  const versionOptions = useMemo(() => {
    const dateMap = {};
    downloadsForDate.forEach((d) => {
      const key = dayjs(d.created_at).format("YYYY-MM-DD HH:mm");
      if (!dateMap[key]) {
        dateMap[key] = { key, ts: d.created_at, brands: new Set(), files: [] };
      }
      dateMap[key].brands.add(d.brand_name);
      dateMap[key].files.push(d);
    });
    return Object.values(dateMap).sort((a, b) => dayjs(b.ts).diff(dayjs(a.ts)));
  }, [downloadsForDate]);

  const resolvedFileIds = useMemo(() => {
    if (!downloadsForDate.length) return [];

    if (selectedVersion === LATEST_KEY) {
      const latestPerBrand = {};
      downloadsForDate.forEach((d) => {
        if (!latestPerBrand[d.brand_id] || dayjs(d.created_at).isAfter(dayjs(latestPerBrand[d.brand_id].created_at))) {
          latestPerBrand[d.brand_id] = d;
        }
      });
      return Object.values(latestPerBrand).map((d) => d.id);
    }

    const match = versionOptions.find((v) => v.key === selectedVersion);
    if (!match) return [];

    const bestPerBrand = {};
    match.files.forEach((d) => {
      if (!bestPerBrand[d.brand_id] || dayjs(d.created_at).isAfter(dayjs(bestPerBrand[d.brand_id].created_at))) {
        bestPerBrand[d.brand_id] = d;
      }
    });
    return Object.values(bestPerBrand).map((d) => d.id);
  }, [downloadsForDate, selectedVersion, versionOptions]);

  const fetchMailingData = useCallback(async () => {
    if (!resolvedFileIds.length) {
      setMailingData([]);
      return;
    }

    setLoading(true);
    try {
      const res = await getMailingResponseDataByFileIds(resolvedFileIds);
      const items = res.data || res || [];
      const rows = Array.isArray(items) ? items : [];

      const merged = rows.map((item, idx) => {
        const fileMeta = fileIdToBrandMeta[item.report_file] || {};
        const brandId = item.brand_id || item.report_file_brand_id || fileMeta.brandId;
        return {
          ...item,
          // API payloads vary by upload version; normalize aliases used by the grid/export templates.
          net_pl_m: item.net_pl_m ?? item.net_profit_loss_per_mail ?? null,
          net_profit_loss_per_mail: item.net_profit_loss_per_mail ?? item.net_pl_m ?? null,
          breakeven_aov: item.breakeven_aov ?? item.be_aov ?? item.be_aov1 ?? null,
          long_term_aov: item.long_term_aov ?? item.lt_aov ?? item.lt_aov1 ?? null,
          be_aov: item.be_aov ?? item.be_aov1 ?? item.breakeven_aov ?? null,
          lt_aov: item.lt_aov ?? item.lt_aov1 ?? item.long_term_aov ?? null,
          brand_name: formatPasRadeBrandName(
            brandNameMapRef.current[brandId] || fileMeta.brandName || item.brand_name || item.company || "Unknown"
          ),
          company: formatPasRadeBrandName(item.company) || item.company,
          _id: `mailing-${item.id || idx}`,
        };
      });

      merged.sort((a, b) => {
        const da = a.mail_date ? dayjs(a.mail_date).valueOf() : 0;
        const db = b.mail_date ? dayjs(b.mail_date).valueOf() : 0;
        return db - da;
      });

      setMailingData(merged);
      setLastUpdated(dayjs().format("MMM DD, YYYY HH:mm:ss"));
    } catch (error) {
      console.error("Error fetching mailing data:", error);
      if (error?.response?.status !== 404) {
        message.error("Failed to fetch mailing/LTV data");
      }
    } finally {
      setLoading(false);
    }
  }, [resolvedFileIds, fileIdToBrandMeta]);

  useEffect(() => {
    fetchMailingData();
  }, [fetchMailingData]);

  const summary = useMemo(() => {
    const stats = { totalRecords: mailingData.length, totalQtyMailed: 0, totalGrossSales: 0, totalNetSales: 0, totalNetPL: 0 };
    mailingData.forEach((item) => {
      stats.totalQtyMailed += parseInt(item.quantity_mailed || item.mailed_quantity || 0, 10);
      stats.totalGrossSales += parseFloat(item.gross_sales || 0);
      stats.totalNetSales += parseFloat(item.net_sales || 0);
      stats.totalNetPL += parseFloat(item.net_profit_loss || 0);
    });
    return stats;
  }, [mailingData]);

  const plChartRef = useRef(null);
  const plChartInstance = useRef(null);

  const plByCode = useMemo(() => {
    const byCode = {};
    mailingData.forEach((item) => {
      const code = item.mailing_code || "Unknown";
      if (!byCode[code]) byCode[code] = 0;
      byCode[code] += parseFloat(item.net_profit_loss || 0);
    });
    return Object.entries(byCode)
      .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
      .slice(0, 15)
      .reduce((acc, [k, v]) => { acc[k] = v; return acc; }, {});
  }, [mailingData]);

  useEffect(() => {
    if (!plChartRef.current || Object.keys(plByCode).length === 0) return;
    if (plChartInstance.current) plChartInstance.current.destroy();

    const labels = Object.keys(plByCode);
    const data = labels.map((l) => plByCode[l]);

    plChartInstance.current = new Chart(plChartRef.current, {
      type: "bar",
      plugins: [ChartDataLabels],
      data: {
        labels,
        datasets: [{
          label: "Net P/L ($)",
          data,
          backgroundColor: data.map((v) => (v >= 0 ? "#52c41a" : "#ff4d4f")),
          borderRadius: 4,
          maxBarThickness: 22,
        }],
      },
      options: {
        indexAxis: "y",
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { right: 48, top: 4, bottom: 4 } },
        plugins: {
          legend: { display: false },
          datalabels: {
            anchor: "end",
            align: "right",
            clamp: true,
            formatter: (val) => formatCompactCurrency(val),
            font: { size: 10, weight: "bold" },
            color: "#334155",
          },
        },
        scales: {
          x: { ticks: { callback: (v) => formatCompactCurrency(v) }, grid: { color: "#f0f0f0" } },
          y: { ticks: { font: { size: 10 }, autoSkip: false }, grid: { display: false } },
        },
      },
    });

    return () => { if (plChartInstance.current) plChartInstance.current.destroy(); };
  }, [plByCode]);

  const handleFetchPasRade = useCallback(async () => {
    setFetchingPasRade(true);
    setFetchProgress("Loading brand list...");
    try {
      let brands;
      try {
        const bRes = await listPasRadeBrands();
        brands = (bRes.data || []).filter((b) => brandIds.includes(b.brand_id));
      } catch {
        brands = brandIds.map((id) => ({ brand_id: id, brand_name: brandNameMap[id] || `Brand ${id}` }));
      }

      if (!brands.length) {
        message.warning("No PAS RADE credentials configured for selected brands");
        return;
      }

      let totalRows = 0;
      let successCount = 0;

      for (let i = 0; i < brands.length; i++) {
        const b = brands[i];
        setFetchProgress(`Fetching ${b.brand_name} (${i + 1}/${brands.length})...`);
        try {
          const res = await triggerPasRadeFetch(b.brand_id);
          const data = res.data || res;
          const rows = data.rows_inserted || 0;
          totalRows += rows;
          if (data.status === "success") successCount++;
          message.success(`${b.brand_name}: ${rows} rows`);
        } catch (err) {
          const detail = err.response?.data?.error || err.message;
          message.error(`${b.brand_name} failed: ${detail}`);
        }
      }

      setFetchProgress("");
      if (totalRows > 0) {
        message.success(`Done! ${successCount}/${brands.length} brands fetched, ${totalRows} total rows`);
      } else {
        message.info("Fetch completed but 0 rows were stored");
      }
      await fetchDownloadsList(true);
    } catch (error) {
      const detail = error.response?.data?.error || error.message;
      message.error(`PAS RADE fetch failed: ${detail}`);
    } finally {
      setFetchingPasRade(false);
      setFetchProgress("");
    }
  }, [brandIds, brandNameMap, fetchDownloadsList]);

  const handleExportExcel = useCallback(async () => {
    if (!resolvedFileIds.length) {
      message.warning("No files selected to export");
      return;
    }
    setExportingExcel(true);
    try {
      message.loading({ content: "Merging original PAS RADE files...", key: "mailing-excel", duration: 0 });
      const res = await exportMergedMailingLtvExcel(resolvedFileIds);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `MERGED-MAILING-RESPONSE-${dayjs().format("YYYY-MM-DD")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success({ content: "Excel exported successfully", key: "mailing-excel" });
    } catch (e) {
      console.error(e);
      const detail = e.response?.data?.error || e.message || "Excel export failed";
      message.error({ content: detail, key: "mailing-excel" });
    } finally {
      setExportingExcel(false);
    }
  }, [resolvedFileIds]);

  const handleExportPDF = useCallback(() => {
    const rows = getFilteredRowsFromGrid(gridRef.current, mailingData);
    exportToPDF(rows, columnDefs, "mailing-ltv", "Merged Mailing/LTV Response", gridRef.current);
    message.success("PDF exported successfully");
  }, [mailingData, columnDefs]);

  const handleExportCSV = useCallback(() => {
    const rows = getFilteredRowsFromGrid(gridRef.current, mailingData);
    if (!rows.length) { message.warning("No rows to export"); return; }
    exportTemplateToCsv(rows, MAILING_LTV_EXPORT_TEMPLATE, "mailing-ltv");
    message.success("CSV exported successfully");
  }, [mailingData]);

  if (!brandIds.length) {
    return <Empty description="Select a client/brand from the global filter to view mailing/LTV data" />;
  }

  return (
    <Spin spinning={loading}>
      {/* Toolbar */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <Space wrap>
            <Button type="primary" icon={<ReloadOutlined />} onClick={fetchMailingData}>Refresh</Button>
            <Button
              icon={<CloudDownloadOutlined />}
              onClick={handleFetchPasRade}
              loading={fetchingPasRade}
              style={{ color: "#eb2f96", borderColor: "#eb2f96" }}
            >
              {fetchProgress || "Fetch PAS RADE Report"}
            </Button>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleExportCSV} disabled={!mailingData.length}>CSV</Button>
            <Button size="small" icon={<FileExcelOutlined />} onClick={handleExportExcel} loading={exportingExcel} disabled={!resolvedFileIds.length} style={{ color: "#52c41a", borderColor: "#52c41a" }}>Excel</Button>
            <Button size="small" icon={<FilePdfOutlined />} onClick={handleExportPDF} disabled={!mailingData.length} style={{ color: "#ff4d4f", borderColor: "#ff4d4f" }}>PDF</Button>
          </Space>

          <Space wrap>
            <CalendarOutlined />
            <Text strong style={{ fontSize: 12 }}>Date:</Text>
            <DatePicker
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={(d) => {
                setSelectedDate(d ? d.format("YYYY-MM-DD") : null);
                setSelectedVersion(LATEST_KEY);
              }}
              disabledDate={(current) => !availableDates.includes(current.format("YYYY-MM-DD"))}
              allowClear={false}
              style={{ width: 140 }}
            />
            <HistoryOutlined />
            <Text strong style={{ fontSize: 12 }}>Version:</Text>
            <Select
              style={{ minWidth: 260 }}
              value={selectedVersion}
              onChange={(val) => setSelectedVersion(val)}
              loading={loadingDownloads}
            >
              <Option value={LATEST_KEY}>
                <Tag color="green" style={{ marginRight: 4 }}>Latest</Tag>
                Most recent on date
              </Option>
              {versionOptions.map((v) => (
                <Option key={v.key} value={v.key}>
                  {dayjs(v.ts).format("h:mm A")}
                  <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                    ({[...v.brands].join(", ")})
                  </Text>
                </Option>
              ))}
            </Select>
          </Space>
        </div>
      </Card>

      {/* Brand file info */}
      {resolvedFileIds.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Space wrap size={4}>
            <Text type="secondary" style={{ fontSize: 11 }}>Loaded files:</Text>
            {downloads
              .filter((d) => resolvedFileIds.includes(d.id))
              .map((d) => (
                <Tag key={d.id} color="blue" style={{ fontSize: 11 }}>
                  {formatPasRadeBrandName(d.brand_name)}: {dayjs(d.created_at).format("MMM DD h:mm A")}
                </Tag>
              ))}
          </Space>
        </div>
      )}

      {/* Summary Statistics */}
      {mailingData.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={5}>
              <Statistic title="Total Records" value={summary.totalRecords} prefix={<FileTextOutlined />} valueStyle={{ color: "#1890ff" }} />
            </Col>
            <Col span={5}>
              <Statistic title="Qty Mailed" value={summary.totalQtyMailed} prefix={<MailOutlined />} valueStyle={{ color: "#52c41a" }} />
            </Col>
            <Col span={5}>
              <Statistic title="Gross Sales" value={summary.totalGrossSales} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: "#722ed1" }} />
            </Col>
            <Col span={5}>
              <Statistic title="Net Sales" value={summary.totalNetSales} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: "#fa8c16" }} />
            </Col>
            <Col span={4}>
              <Statistic title="Net P/L" value={summary.totalNetPL} prefix={<DollarOutlined />} precision={2} valueStyle={{ color: summary.totalNetPL >= 0 ? "#52c41a" : "#ff4d4f" }} />
            </Col>
          </Row>
        </Card>
      )}

      {/* Net P/L by Mailing Code Chart */}
      {Object.keys(plByCode).length > 0 && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <Card title="Net P/L by Mailing Code (Top 15)" size="small">
              <div style={{ height: Math.max(320, Object.keys(plByCode).length * 28) }}>
                <canvas ref={plChartRef} />
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* AG Grid */}
      <Card title="Mailing Response & LTV Data - All Clients" size="small">
        {mailingData.length === 0 && !loading ? (
          <Empty description={
            downloads.length === 0
              ? "No LTV downloads available. Click 'Fetch PAS RADE Report' to download."
              : "No data found for the selected version."
          } />
        ) : (
          <div className="ag-theme-alpine" style={{ height: "calc(100vh - 200px)", minHeight: 500, width: "100%" }}>
            <AgGridWithColumnState
              tableId="sales-dashboard-mailing-ltv-v2"
              gridApiRef={gridRef}
              rowData={mailingData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={100}
              paginationPageSizeSelector={[50, 100, 200, 500]}
              enableRangeSelection={true}
              enableCellTextSelection={true}
              suppressMovableColumns={false}
              animateRows={false}
              getRowId={(params) => params.data._id}
              sideBar={{
                toolPanels: [
                  { id: "columns", labelDefault: "Columns", labelKey: "columns", iconKey: "columns", toolPanel: "agColumnsToolPanel" },
                  { id: "filters", labelDefault: "Filters", labelKey: "filters", iconKey: "filter", toolPanel: "agFiltersToolPanel" },
                ],
              }}
            />
          </div>
        )}
      </Card>
    </Spin>
  );
};

export default MergedMailingLTV;
